import type { UserHydraCollectionItem } from "@verbleif/lib/src/composables/Api/Type/User/user.interface";
import type { AggregateVoterCheck } from "./composables/ContextVoter.interface";
import { PermissionScope } from "@verbleif/lib/src/composables/Api/Type/UserPermissions/userPermission.enum";
import { PermissionAttributesEnum } from "@verbleif/lib/src/composables/Permissions/permissions.enum";
import { ContextVoter } from "./composables/ContextVoter";

export class UserVoter extends ContextVoter {
  public canImpersonate = () => {
    return !!this.permissionService.getGlobalPermissionForUser({
      attribute: PermissionAttributesEnum.GlobalUserSwitchuserany,
    });
  };

  public canUpdateAllowedApps = () => {
    return !!this.permissionService.getGlobalPermissionForUser({
      attribute: PermissionAttributesEnum.GlobalUserUpdateFieldAllowedapps,
    });
  };

  /** The API writes a new password for another user only with this field right, next to updating that user. */
  public canUpdatePlainPassword = () => {
    return !!this.permissionService.getGlobalPermissionForUser({
      attribute: PermissionAttributesEnum.GlobalUserUpdateFieldPlainpassword,
    });
  };

  public canInvite = ({
    location = this.permissionService.defaultLocation,
    client = this.permissionService.defaultClient,
    scope,
  }: {
    location?: string | null
    client?: string | null
    scope: Extract<PermissionScope, PermissionScope.CLIENT | PermissionScope.LOCATION>
  }) => {
    if (this.permissionService.getGlobalPermissionForUser({
      attribute: PermissionAttributesEnum.GlobalOperationUserInviteany,
    })) {
      return true;
    }

    if (this.hasAccessToScope({
      location,
      client,
      scope,
      attribute: PermissionAttributesEnum.ContextOperationUserInvite,
    }) === false) {
      return false;
    }

    return true;
  };

  public canRead = ({
    // location = this.permissionService.defaultLocation,
    // client = this.permissionService.defaultClient,
    item,
  }: {
    location?: string | null
    client?: string | null
    item: UserHydraCollectionItem
  }) => {
    if (this.permissionService.getGlobalPermissionForUser({
      attribute: PermissionAttributesEnum.GlobalOperationUserReadany,
    })) {
      return true;
    }

    // If the user is the same as the item, they can always true
    if (this.permissionService.userIri === item["@id"]) {
      return true;
    }

    return this.hasAccessViaSubject({
      subject: item,
      attribute: PermissionAttributesEnum.ContextOperationUserRead,
    });
  };

  public canUpdate = (
    {
      location = this.permissionService.defaultLocation,
      client = this.permissionService.defaultClient,
      item,
      scope,
    }: {
      location?: string | null
      client?: string | null
      item: UserHydraCollectionItem
      scope: Extract<PermissionScope, PermissionScope.CLIENT | PermissionScope.LOCATION>
    },
  ): boolean => {
    if (this.permissionService.getGlobalPermissionForUser({
      attribute: PermissionAttributesEnum.GlobalOperationUserUpdateany,
    })) {
      return true;
    }

    if (this.hasAccessToScope({
      location,
      client,
      scope,
      attribute: PermissionAttributesEnum.ContextOperationUserUpdate,
    }) === false) {
      return false;
    }

    // If the user is the same as the item, they can always true
    if (this.permissionService.userIri === item["@id"]) {
      return true;
    }

    return this.hasAccessViaSubject({
      subject: item,
      attribute: PermissionAttributesEnum.ContextOperationUserUpdate,
    });
  };

  public canManage: AggregateVoterCheck = (item = {
    client: this.permissionService.defaultClient,
    location: this.permissionService.defaultLocation,
  }) => {
    return this._canCrud({
      item,
      globalAttribute: [
        PermissionAttributesEnum.GlobalOperationUserInviteany,
        PermissionAttributesEnum.GlobalOperationUserUpdateany,
      ],
      contextAttribute: [
        PermissionAttributesEnum.ContextOperationUserInvite,
        PermissionAttributesEnum.ContextOperationUserUpdate,
        PermissionAttributesEnum.ContextOperationUserpermissionUpdate,
        PermissionAttributesEnum.ContextOperationUserpermissionDelete,
      ],
    });
  };

  // protected hasAccess(
  //   {
  //     "@id": iri,
  //     location,
  //     client,
  //     attribute,
  //     scope,
  //   }: {
  //     "@id"?: string
  //     "location"?: string | null
  //     "client"?: string | null
  //     "attribute": PermissionAttributesEnum
  //     "scope"?: Exclude<PermissionScope, PermissionScope.GLOBAL>
  //   },
  // ): boolean {
  //   if (iri && scope) {
  //     const userPermission = this.permissionService.getScopePermissionForUser({
  //       iri,
  //       attribute,
  //       scope,
  //     });
  //     if (userPermission !== null) {
  //       return true;
  //     }
  //   }

  //   if (location) {
  //     const userLocationPermission = this.permissionService.getLocationPermissionForUser({ locationIri: location, attribute });
  //     if (userLocationPermission !== null) {
  //       return true;
  //     }
  //   }

  //   const inputClient = client ?? (location ? this.permissionService.getClientViaLocation(location) : null);

  //   if (inputClient === null) {
  //     return false;
  //   }

  //   const clientPermission = this.permissionService.getClientPermissionForUser({ clientIri: inputClient, attribute });

  //   return clientPermission !== null;
  // }

  protected hasAccessToScope({
    location,
    client,
    scope,
    attribute,
  }: {
    location?: string | null
    client?: string | null
    scope?: Extract<PermissionScope, PermissionScope.CLIENT | PermissionScope.LOCATION>
    attribute?: PermissionAttributesEnum
  }): boolean {
    if (!scope) {
      return false;
    }

    // Check client permissions first
    const inputClient = client ?? (location ? this.permissionService.getClientViaLocation(location) : null);
    if (inputClient) {
      const clientPermission = this.permissionService.getClientPermissionForUser({
        clientIri: inputClient,
        attribute,
      });

      if (clientPermission !== null) {
        return true;
      }
    }

    // If scope is location and we have a location, check location permissions
    if (scope === PermissionScope.LOCATION && location) {
      const locationPermission = this.permissionService.getLocationPermissionForUser({
        locationIri: location,
        attribute,
      });

      if (locationPermission !== null) {
        return true;
      }
    }

    return false;
  }

  protected hasAccessViaSubject(
    {
      subject,
      attribute,
    }: {
      subject: UserHydraCollectionItem // The user we're checking permissions for
      attribute: PermissionAttributesEnum // The permission attribute we're checking
    },
  ): boolean {
    // Get all the IDs that the current user has access to for each scope type
    const clientIds = this.permissionService.getAllIdentitiesViaScope({
      scope: PermissionScope.CLIENT, // Get client-level permissions
      attribute,
      column: "client", // Look up by client column
    });
    const locationIds = this.permissionService.getAllIdentitiesViaScope({
      scope: PermissionScope.LOCATION, // Get location-level permissions
      attribute,
      column: "location", // Look up by location column
    });
    const userGroupIds = this.permissionService.getAllIdentitiesViaScope({
      scope: PermissionScope.USER_GROUP, // Get user group-level permissions
      attribute,
      column: "userGroup", // Look up by userGroup column
    });
    const propertyIds = this.permissionService.getAllIdentitiesViaScope({
      scope: PermissionScope.PROPERTY, // Get property-level permissions
      attribute,
      column: "property", // Look up by property column
    });

    // If the subject has no statuses at all, they can't have access
    if (!subject.statuses || subject.statuses.length === 0) {
      return false; // No statuses to check against
    }
    // Check each status of the subject against our allowed IDs
    for (const status of subject.statuses) {
      // Check if subject has a client status that matches one we have access to
      if (status.client && clientIds.includes(status.client)) {
        return true; // Found matching client status
      }
      // Check if subject has a location status that matches one we have access to
      if (status.location && locationIds.includes(status.location)) {
        return true; // Found matching location status
      }
      // Check if subject has a user group status that matches one we have access to
      if (status.userGroup && userGroupIds.includes(status.userGroup)) {
        return true; // Found matching user group status
      }
      // Check if subject has a property status that matches one we have access to
      if (status.property && propertyIds.includes(status.property)) {
        return true; // Found matching property status
      }
    }

    // We checked all statuses and found no matches
    return false; // No matching statuses found
  }
}
